import React, { useState, useEffect } from 'react';
import { getActiveBanks, submitWalletRecharge, getRechargeRequests, getMerchantRechargePaymentProof } from '../../services/api';
import { formatDateDDMMYYYY } from '../../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';

const PAGE_SIZE = 10;

const STATUS_STYLES = {
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  PENDING:  'bg-amber-100 text-amber-800',
};

const Wallet = () => {
  const [banks, setBanks]         = useState([]);
  const [allRequests, setAllRequests] = useState([]); // Store all fetched requests
  const [requests, setRequests]   = useState([]);   // Current page requests
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState({ bankId: '', amount: '', transactionReference: '', remarks: '' });
  const [file, setFile]           = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage]           = useState(0); // 0-indexed
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [downloadingProofId, setDownloadingProofId] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch banks and a large batch of requests (1000) for client-side pagination
      const [banksRes, reqsRes] = await Promise.all([getActiveBanks(), getRechargeRequests(0, 1000)]);
      setBanks(banksRes.data || []);

      let data = [];
      let totalRecs = 0;

      // Handle API response - check if it's paginated or direct array
      if (Array.isArray(reqsRes.data)) {
        data = reqsRes.data;
        totalRecs = reqsRes.data.length;
      } else if (reqsRes.data && typeof reqsRes.data === 'object') {
        const responseData = reqsRes.data;
        data = responseData.content || responseData.data || [];
        totalRecs = responseData.totalRecords || responseData.total || data.length;
      }

      setAllRequests(data);
      setTotalRecords(totalRecs);
      setTotalPages(Math.ceil(data.length / PAGE_SIZE));
      
      // Set first page
      setRequests(data.slice(0, PAGE_SIZE));
      setPage(0);
    } catch (err) {
      showErrorToast('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = [];
    if (!form.bankId)                          errors.push('Please select a bank');
    if (!form.amount || parseFloat(form.amount) <= 0) errors.push('Please enter a valid amount');
    if (!form.transactionReference)            errors.push('Please enter transaction reference');
    if (!form.remarks)                         errors.push('Please enter remarks');
    if (!file)                                 errors.push('Please upload payment proof');
    if (errors.length > 0) { errors.forEach(e => showErrorToast(e)); return; }

    setSubmitting(true);
    try {
      const response = await submitWalletRecharge({
        bankId: form.bankId,
        amount: parseFloat(form.amount),
        transactionReference: form.transactionReference,
        remarks: form.remarks,
        paymentProof: file,
      });

      if (response.data) {
        const { requestId, amount, status } = response.data;
        showSuccessToast(`Request submitted! ID: ${requestId} · ₹${amount?.toLocaleString()} · ${status}`);
      } else {
        showSuccessToast('Recharge request submitted successfully!');
      }

      setForm({ bankId: '', amount: '', transactionReference: '', remarks: '' });
      setFile(null);
      setPage(0);
      await loadData(0);
    } catch (err) {
      let msg = 'Failed to submit recharge request';
      if (err.response?.data?.message)   msg = err.response.data.message;
      else if (err.response?.data?.error) msg = err.response.data.error;
      else if (err.response?.status === 400) msg = 'Invalid request data. Please check all fields.';
      else if (err.response?.status === 401) msg = 'Authentication failed. Please login again.';
      else if (err.response?.status === 403) msg = 'Permission denied.';
      else if (err.message)              msg = err.message;
      showErrorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedBank = banks.find(b => b.id.toString() === form.bankId);

  // Handle page changes
  const handlePageChange = (newPage) => {
    setPage(newPage);
    const startIndex = newPage * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    setRequests(allRequests.slice(startIndex, endIndex));
  };

  const handleExportCSV = () => {
    const listToExport = allRequests.length > 0 ? allRequests : requests;
    if (!listToExport || listToExport.length === 0) {
      showErrorToast('No recharge history data to export');
      return;
    }

    const headers = [
      'Request ID',
      'Bank Name',
      'Amount (INR)',
      'Transaction Reference',
      'Remarks',
      'Requested Date',
      'Status',
      'Rejection Reason'
    ];

    const rows = listToExport.map(r => [
      `"${r.requestId || r.id || ''}"`,
      `"${r.bankName || ''}"`,
      r.amount || 0,
      `"${r.transactionReference || ''}"`,
      `"${(r.remarks || '').replace(/"/g, '""')}"`,
      `"${formatDateDDMMYYYY(r.createdAt)}"`,
      `"${r.status || ''}"`,
      `"${(r.rejectionReason || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wallet_recharge_history_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSuccessToast('Recharge history exported successfully!');
  };

  const handleDownloadProof = async (requestId) => {
    setDownloadingProofId(requestId);
    try {
      const response = await getMerchantRechargePaymentProof(requestId);
      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const fileURL = URL.createObjectURL(blob);
      
      if (contentType.includes('image') || contentType.includes('pdf')) {
        window.open(fileURL, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = `payment-proof-${requestId}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(fileURL);
      }
    } catch (err) {
      console.error('Download proof error:', err);
      showErrorToast('Failed to download payment proof document');
    } finally {
      setDownloadingProofId(null);
    }
  };

  // Calculate display range
  const startRecord = page * PAGE_SIZE + 1;
  const endRecord = Math.min((page + 1) * PAGE_SIZE, totalRecords || allRequests.length);

  if (loading) return (
    <div className="flex justify-center items-center p-8 gap-3">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      <span className="text-gray-600">Loading...</span>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* ── Recharge Form ── */}
      <Card>
        <CardHeader>
          <CardTitle>Recharge Wallet</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-4">

            {/* Bank Select */}
            <div className="sm:col-span-2">
              <Label>Select Bank *</Label>
              <Select value={form.bankId} onValueChange={(v) => setForm({ ...form, bankId: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select bank" />
                </SelectTrigger>
                <SelectContent>
                  {banks.map(b => (
                    <SelectItem key={b.id} value={b.id.toString()}>{b.bankName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bank Details */}
            {selectedBank && (
              <div className="sm:col-span-2 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm grid sm:grid-cols-3 gap-2">
                <p><span className="text-gray-500">Account:</span> <strong>{selectedBank.accountNumber}</strong></p>
                <p><span className="text-gray-500">IFSC:</span> <strong>{selectedBank.ifscCode}</strong></p>
                <p><span className="text-gray-500">Name:</span> <strong>{selectedBank.accountHolderName}</strong></p>
              </div>
            )}

            {/* Amount */}
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                className="mt-1"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="Enter amount"
                min="1"
              />
            </div>

            {/* Transaction Reference */}
            <div>
              <Label>Transaction Reference *</Label>
              <Input
                className="mt-1"
                value={form.transactionReference}
                onChange={(e) => setForm({ ...form, transactionReference: e.target.value })}
                placeholder="Enter transaction reference number"
              />
            </div>

            {/* Remarks */}
            <div>
              <Label>Remarks *</Label>
              <Input
                className="mt-1"
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                placeholder="Enter remarks"
              />
            </div>

            {/* Payment Proof */}
            <div>
              <Label>Payment Proof *</Label>
              <Input
                type="file"
                className="mt-1"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </div>

            {/* Submit */}
            <div className="sm:col-span-2">
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* ── Recharge History ── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recharge History</CardTitle>
            <span className="text-sm text-gray-400">{totalRecords || requests.length} total request{(totalRecords || requests.length) !== 1 ? 's' : ''}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            disabled={allRequests.length === 0 && requests.length === 0}
            className="flex items-center gap-1.5"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {requests.length === 0 ? (
            <p className="p-6 text-center text-gray-400">No recharge requests yet.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
              <div className="min-w-[850px]">
                {/* Table Header */}
                <div className="grid grid-cols-7 gap-2 px-4 py-2 bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <span>Request ID</span>
                  <span>Bank</span>
                  <span>Amount</span>
                  <span>Reference</span>
                  <span>Date</span>
                  <span className="text-center">Status</span>
                  <span className="text-right">Actions</span>
                </div>

                {/* Rows */}
                <div className="divide-y">
                  {requests.map(r => (
                    <div key={r.id} className="grid grid-cols-7 gap-2 px-4 py-3 items-center hover:bg-gray-50 transition-colors">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{r.requestId}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700">{r.bankName || '—'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">₹{r.amount?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 truncate">{r.transactionReference || '—'}</p>
                        {r.remarks && <p className="text-xs text-gray-400 truncate">{r.remarks}</p>}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">{formatDateDDMMYYYY(r.createdAt)}</p>
                        <p className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
                        {r.status === 'REJECTED' && r.rejectionReason && (
                          <p className="text-xs text-red-500 mt-1">↳ {r.rejectionReason}</p>
                        )}
                      </div>
                      <div className="flex justify-center">
                        <Badge className={STATUS_STYLES[r.status] || 'bg-gray-100 text-gray-600'}>
                          {r.status}
                        </Badge>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadProof(r.requestId)}
                          disabled={downloadingProofId === r.requestId}
                          className="h-8 w-8 p-0 text-gray-600 hover:text-blue-600 hover:bg-blue-50"
                          title="Download Payment Proof"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pagination */}
            {requests.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t bg-gray-50 gap-4">
                <p className="text-sm text-gray-500">
                  {totalRecords > 0 ? `${startRecord}-${endRecord} of ${totalRecords}` : `Showing ${requests.length} requests`}
                </p>
                <div className="flex items-center gap-1 overflow-x-auto max-w-full py-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 0}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  {Array.from({ length: Math.max(totalPages, 1) }, (_, i) => (
                    <Button
                      key={i}
                      variant={i === page ? 'default' : 'outline'}
                      size="sm"
                      className="w-8 h-8 p-0 shrink-0"
                      onClick={() => handlePageChange(i)}
                    >
                      {i + 1}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === (totalPages - 1)}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default Wallet;